// Public "suggest a term" flow: search-first (to avoid duplicates), with a
// fallback form for brand-new terms. Every submission is Turnstile-gated and
// lands in the admin's pending queue.
import { api, ApiError } from './api';
import { loadPayload, refreshPendingCount } from './data';
import { $, debounce, el } from './dom';
import { state } from './state';
import { toast } from './toast';
import { getTurnstileToken } from './turnstile';

let polygonId: number | null = null;

export function initSuggestDialog(): void {
  const search = $<HTMLInputElement>('#suggestSearch');
  search.addEventListener('input', debounce(() => void renderResults(), 200));

  $<HTMLFormElement>('#suggestNewForm').addEventListener('submit', (e) => {
    e.preventDefault();
    const text = $<HTMLInputElement>('#suggestNewText').value.trim();
    if (!text) return;
    const synonyms = $<HTMLInputElement>('#suggestNewSynonyms')
      .value.split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    void submit({ text, synonyms });
  });
}

export function openSuggestDialog(forPolygonId: number): void {
  polygonId = forPolygonId;
  $<HTMLInputElement>('#suggestSearch').value = '';
  $<HTMLInputElement>('#suggestNewText').value = '';
  $<HTMLInputElement>('#suggestNewSynonyms').value = '';
  $<HTMLDetailsElement>('#suggestNewWrap').open = false;
  $<HTMLDialogElement>('#suggestDialog').showModal();
  void renderResults();
}

async function renderResults(): Promise<void> {
  const q = $<HTMLInputElement>('#suggestSearch').value.trim();
  const results = $('#suggestResults');
  let terms;
  try {
    terms = await api.searchTerms(q);
  } catch {
    return;
  }
  results.textContent = '';
  const poly = state.payload?.polygons.find((p) => p.id === polygonId);
  const connected = new Set(poly?.connections.map((c) => c.term_id) ?? []);
  for (const term of terms.slice(0, 30)) {
    const li = el('li');
    li.appendChild(el('span', 'term-name', term.text));
    if (term.synonyms.length) li.appendChild(el('span', 'syns', term.synonyms.join(', ')));
    if (connected.has(term.id)) {
      li.appendChild(el('span', 'chip', 'already connected'));
    } else {
      const btn = el('button', 'accent-btn', 'suggest');
      btn.style.marginLeft = 'auto';
      btn.addEventListener('click', () => void submit({ term_id: term.id }));
      li.appendChild(btn);
    }
    results.appendChild(li);
  }
  if (terms.length === 0) {
    results.appendChild(el('li', 'dim', q ? 'No matching terms — propose a new one below.' : 'No terms yet.'));
  }
}

async function submit(body: { term_id?: number; text?: string; synonyms?: string[] }): Promise<void> {
  if (polygonId === null) return;
  try {
    const token = await getTurnstileToken('suggest');
    if (!token) return;
    await api.suggest(polygonId, { ...body, token });
    $<HTMLDialogElement>('#suggestDialog').close();
    toast('Suggestion submitted — it will appear once the admin approves it.', 'ok');
    void refreshPendingCount();
    if (state.admin) void loadPayload(); // admins see the pending connection immediately
  } catch (err) {
    toast(err instanceof ApiError ? err.message : 'submission failed', 'err');
  }
}
